import jsPDF from "jspdf";
import logo from "../assets/icons/qvh-logo.svg";

// Normalize ALL strings (fixes spacing glitches with ä ö ü etc.)
function normalizeText(text) {
  return typeof text === "string" ? text.normalize("NFC") : text;
}

function clean(obj) {
  if (!obj) return obj;
  if (typeof obj === "string") return obj.normalize("NFC");
  if (Array.isArray(obj)) return obj.map((x) => clean(x));
  if (typeof obj === "object") {
    const out = {};
    for (const k in obj) out[k] = clean(obj[k]);
    return out;
  }
  return obj;
}

export function generateDiagnosePdf(resultRaw) {
  const result = clean(resultRaw);
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = 210;
  const pageHeight = 297;
  const margin = 20;
  const columnGap = 10;
  const columnWidth = (pageWidth - margin * 2 - columnGap) / 2;
  let y = 20;
  const today = new Date().toLocaleDateString("de-DE");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  const addPage = () => {
    doc.addPage();
    y = 20;
    doc.setFontSize(10);
  };

  const ensureSpace = (height) => {
    if (y + height > pageHeight - 20) addPage();
  };

  const sectionTitle = (text) => {
    ensureSpace(16);
    doc.setFillColor(235, 235, 235);
    doc.rect(margin - 2, y - 6, pageWidth - margin * 2 + 4, 9, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(normalizeText(text), margin, y);
    doc.setFontSize(10);
    y += 14;
  };

  const drawFullBox = (title, contentFn) => {
    const startY = y;
    doc.setFont("helvetica", "bold");
    doc.text(normalizeText(title), margin, y);
    y += 6;
    contentFn();
    const boxHeight = y - startY + 6;
    doc.setDrawColor(200);
    doc.rect(margin - 3, startY - 6, pageWidth - margin * 2 + 6, boxHeight);
    y += 10;
  };

  const drawColumnBox = (x, startY, title, data) => {
    let localY = startY;
    doc.setFont("helvetica", "bold");
    doc.text(normalizeText(title), x, localY);
    localY += 6;

    const drawRow = (label, value, highlight = false) => {
      doc.setFont("helvetica", "bold");
      doc.text(normalizeText(`${label}:`), x, localY);
      doc.setFont("helvetica", "normal");
      if (highlight) doc.setTextColor(180, 0, 0);
      if (value) {
        const lines = doc.splitTextToSize(
          normalizeText(String(value)),
          columnWidth - 5
        );
        doc.text(lines, x, localY + 4);
        localY += lines.length * 5 + 6;
      } else {
        localY += 8;
      }
      doc.setTextColor(0, 0, 0);
    };

    drawRow("Risikogruppe", data.risk, data.risk?.includes("Risikogruppe"));
    drawRow("Symptome", data.symptoms);
    drawRow("Kriterien", data.criteria);
    drawRow("Bewertung", data.bewertung);


    doc.setDrawColor(200);
    doc.rect(x - 3, startY - 6, columnWidth + 6, localY - startY + 8);
    return localY;
  };

  const img = new Image();
  img.src = logo;

  img.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = 414; canvas.height = 177;
    canvas.getContext("2d").drawImage(img, 0, 0, 414, 177);
    const pngData = canvas.toDataURL("image/png");
    const imgWidth = 40;
    const imgHeight = (177 / 414) * imgWidth;
    const imgX = (pageWidth - imgWidth) / 2;
    doc.addImage(pngData, "PNG", imgX, y, imgWidth, imgHeight);

    doc.setFontSize(9);
    doc.text(`Datum: ${today}`, pageWidth - margin, y + 5, {
      align: "right",
    });

    y += imgHeight + 10;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Bewertung", pageWidth / 2, y, { align: "center" });
    doc.setFontSize(10);
    y += 16;

    sectionTitle("Fußbewertung");
    ensureSpace(80);
    const startY = y;

    const leftEnd = drawColumnBox(margin, startY, "Linker Fuß", {
      risk: result.risk_groups_left,
      symptoms: result.symptoms_left?.join(", "),
      criteria: result.criteria_left?.join(", "),
      bewertung: result.left_description,
      // rezept removed from object too (not needed anymore)
    });

    const rightEnd = drawColumnBox(
      margin + columnWidth + columnGap,
      startY,
      "Rechter Fuß",
      {
        risk: result.risk_groups_right,
        symptoms: result.symptoms_right?.join(", "),
        criteria: result.criteria_right?.join(", "),
        bewertung: result.right_description,
        // rezept removed from object too
      }
    );

    y = Math.max(leftEnd, rightEnd) + 12;

    sectionTitle("Gesamtbewertung");

    drawFullBox("Gesamtbewertung", () => {
      // Höchste Risikogruppe
      doc.setFont("helvetica", "bold");
      doc.text("Höchste Risikogruppe:", margin, y);
      if (result.higher_risk_group?.includes("Risikogruppe")) {
        doc.setTextColor(180, 0, 0);
      }
      doc.setFont("helvetica", "normal");
      doc.text(result.higher_risk_group || "", margin + 40, y);
      doc.setTextColor(0, 0, 0);
      y += 8;

      // Bewertung (description)
      if (result.higher_risk_recipe_description) {
        doc.setFont("helvetica", "bold");
        doc.text("Bewertung:", margin, y);
        y += 5;
        const lines = doc.splitTextToSize(
          normalizeText(result.higher_risk_recipe_description),
          pageWidth - margin * 2
        );
        doc.setFont("helvetica", "normal");
        doc.text(lines, margin, y);
        y += lines.length * 5 + 6;
      }

      // Rezeptvorschlag –  (for highest risk group)
      if (result.higher_risk_recipe_suggestion) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text("Rezeptvorschlag:", margin, y);
        y += 5;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        const lines = doc.splitTextToSize(
          normalizeText(String(result.higher_risk_recipe_suggestion)),
          pageWidth - margin * 2
        );
        doc.text(lines, margin, y);
        y += lines.length * 5 + 6;
      }

      // Source disclaimer
      if (result.higher_risk_group_source) {
        const sourceText =
          result.higher_risk_group_source === "left"
            ? "linken Fußes"
            : result.higher_risk_group_source === "right"
            ? "rechten Fußes"
            : "";
        const disclaimer = `Hinweis: Diese Gesamtbewertung basiert auf dem Befund des ${sourceText}, da dieser Fuß die höchste Risikogruppe aufweist.`;
        const lines = doc.splitTextToSize(
          normalizeText(disclaimer),
          pageWidth - margin * 2 - 10
        );
        doc.setFont("helvetica", "bold");
        doc.text(lines, margin, y);
        doc.setFont("helvetica", "normal");
        y += lines.length * 5 + 6;
      }
    });

    doc.setFontSize(8);
    doc.text(
      "Hinweis: Diese Bewertung ersetzt nicht die ärztliche Beurteilung.",
      margin,
      pageHeight - 12
    );

    // Universal cross-browser download using Blob
    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);

    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = "Bewertung.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // Cleanup memory
    setTimeout(() => {
      URL.revokeObjectURL(pdfUrl);
    }, 1000);
      };
}